import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  formatEuro,
  isoToDateInput,
  parseDateToIso,
  parseEuroToCentimes,
  parseM2ToCentiM2,
} from '../lib/format';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import type { LigneVente, TypeAvoir } from '@cockpit/shared';

interface LigneAvoirRow {
  ligne_vente_id: number | null;
  libelle: string;
  quantite_m2_str: string; // en m² affichage
  prix_str: string; // en € / m² affichage
  taux_tva_str: string; // en pct affichage
}

function emptyRow(): LigneAvoirRow {
  return {
    ligne_vente_id: null,
    libelle: '',
    quantite_m2_str: '',
    prix_str: '',
    taux_tva_str: '20,00',
  };
}

function rowFromLigneVente(l: LigneVente): LigneAvoirRow {
  return {
    ligne_vente_id: l.id,
    libelle: l.reference_libre ?? `Ligne #${String(l.id)}${l.reference_id !== null ? '' : ''}`,
    quantite_m2_str: (l.m2_centi / 100).toFixed(2).replace('.', ','),
    prix_str: (l.prix_unitaire_centimes_par_m2 / 100).toFixed(2).replace('.', ','),
    taux_tva_str: (l.taux_tva / 100).toFixed(2).replace('.', ','),
  };
}

export function AvoirNouveauPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const venteId = Number.parseInt(id ?? '0', 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: venteData } = useQuery({
    queryKey: ['vente', venteId],
    queryFn: () => api.ventes.get(venteId),
    enabled: venteId > 0,
  });

  const [dateEmission, setDateEmission] = useState(isoToDateInput(new Date().toISOString()));
  const [motif, setMotif] = useState('');
  const [typeAvoir, setTypeAvoir] = useState<TypeAvoir>('Credit');
  const [mode, setMode] = useState<'global' | 'partiel'>('global');
  const [rows, setRows] = useState<LigneAvoirRow[]>([emptyRow()]);
  const [error, setError] = useState<string | null>(null);

  // Lorsqu'on bascule en mode "partiel", pré-remplit avec les lignes de la vente.
  function onModeChange(newMode: 'global' | 'partiel'): void {
    setMode(newMode);
    if (newMode === 'partiel' && venteData !== undefined) {
      setRows(venteData.lignes.map(rowFromLigneVente));
    } else if (newMode === 'global') {
      setRows([emptyRow()]);
    }
  }

  const totaux = useMemo(() => {
    let ht = 0;
    let tva = 0;
    for (const r of rows) {
      const q = parseM2ToCentiM2(r.quantite_m2_str) ?? 0;
      const p = parseEuroToCentimes(r.prix_str) ?? 0;
      const tauxF = Number.parseFloat(r.taux_tva_str.replace(',', '.'));
      const taux = Number.isFinite(tauxF) ? Math.round(tauxF * 100) : 2000;
      const ligneHt = Math.round((q * p) / 100);
      const ligneTva = Math.round((ligneHt * taux) / 10_000);
      ht += ligneHt;
      tva += ligneTva;
    }
    return { ht, tva, ttc: ht + tva };
  }, [rows]);

  const mutation = useMutation({
    mutationFn: async () => {
      const dateIso = parseDateToIso(dateEmission);
      if (dateIso === null) throw new Error('Date invalide');
      if (rows.length === 0) throw new Error('Au moins une ligne requise');
      const lignes = rows.map((r, idx) => {
        const q = parseM2ToCentiM2(r.quantite_m2_str);
        const p = parseEuroToCentimes(r.prix_str);
        const tauxF = Number.parseFloat(r.taux_tva_str.replace(',', '.'));
        if (q === null || q < 0) throw new Error(`Quantité ligne ${String(idx + 1)} invalide`);
        if (p === null || p < 0) throw new Error(`Prix ligne ${String(idx + 1)} invalide`);
        if (!Number.isFinite(tauxF)) throw new Error(`TVA ligne ${String(idx + 1)} invalide`);
        if (r.libelle.trim() === '') throw new Error(`Libellé ligne ${String(idx + 1)} requis`);
        return {
          ligne_vente_id: r.ligne_vente_id,
          libelle: r.libelle.trim(),
          quantite_centi_m2: q,
          prix_unitaire_centimes_par_m2: p,
          taux_tva: Math.round(tauxF * 100),
          ordre: idx,
        };
      });
      return api.avoirs.create({
        vente_id: venteId,
        numero_avoir_henrri: null,
        date_emission: dateIso,
        motif: motif === '' ? null : motif,
        type_avoir: typeAvoir,
        notes: null,
        lignes,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['avoirs'] });
      void queryClient.invalidateQueries({ queryKey: ['vente', venteId] });
      navigate(`/ventes/${String(venteId)}`);
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'),
  });

  function updateRow(idx: number, patch: Partial<LigneAvoirRow>): void {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">Créer un avoir</h1>
        <p className="text-xs text-slate-500 mt-1">
          Vente #{venteId} · {venteData?.vente.numero_facture_henrri ?? '—'}
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Input
          label="Date d'émission"
          type="date"
          value={dateEmission}
          onChange={(e): void => setDateEmission(e.target.value)}
        />
        <Select
          label="Type d'avoir"
          value={typeAvoir}
          onChange={(e): void => setTypeAvoir(e.target.value as TypeAvoir)}
          options={[
            { value: 'Credit', label: 'Crédit (utilisable sur prochaine commande)' },
            { value: 'Remboursement', label: 'Remboursement (sortie cash)' },
          ]}
        />
        <Input label="Motif" value={motif} onChange={(e): void => setMotif(e.target.value)} />
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-200 mb-2">Lignes de l'avoir</h2>
        <div className="flex gap-2 mb-3">
          <Button
            size="sm"
            variant={mode === 'global' ? 'primary' : 'secondary'}
            onClick={(): void => onModeChange('global')}
          >
            Avoir global
          </Button>
          <Button
            size="sm"
            variant={mode === 'partiel' ? 'primary' : 'secondary'}
            onClick={(): void => onModeChange('partiel')}
          >
            Avoir partiel (par ligne)
          </Button>
        </div>

        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-end border border-slate-800 rounded-md p-2"
            >
              <div className="col-span-4">
                <Input
                  label="Libellé"
                  value={r.libelle}
                  onChange={(e): void => updateRow(idx, { libelle: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Input
                  label="Quantité (m²)"
                  value={r.quantite_m2_str}
                  onChange={(e): void => updateRow(idx, { quantite_m2_str: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Input
                  label="Prix (€/m²)"
                  value={r.prix_str}
                  onChange={(e): void => updateRow(idx, { prix_str: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Input
                  label="TVA (%)"
                  value={r.taux_tva_str}
                  onChange={(e): void => updateRow(idx, { taux_tva_str: e.target.value })}
                />
              </div>
              <div className="col-span-2 flex justify-end">
                {rows.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(): void => setRows((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={(): void => setRows((prev) => [...prev, emptyRow()])}
          >
            + Ajouter une ligne
          </Button>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 text-sm grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-slate-500">Total HT</div>
          <div className="font-mono">{formatEuro(totaux.ht)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">TVA</div>
          <div className="font-mono">{formatEuro(totaux.tva)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Total TTC</div>
          <div className="font-mono font-semibold">{formatEuro(totaux.ttc)}</div>
        </div>
      </div>

      {error !== null && <p className="text-xs text-red-400">{error}</p>}

      <div className="flex gap-2">
        <Button onClick={(): void => navigate(`/ventes/${String(venteId)}`)} variant="ghost">
          Annuler
        </Button>
        <Button
          onClick={(): void => {
            setError(null);
            mutation.mutate();
          }}
          disabled={mutation.isPending}
        >
          Créer l'avoir
        </Button>
      </div>
    </div>
  );
}
